/* eslint-disable no-bitwise */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';

import { getCssVariable } from 'uikit/utils';
import { Icon } from 'uikit/icons';

import styles from '../styles/uikitAvatarsAvatar.module.scss';

const Avatar = ({
  ariaLabel = '',
  ariaHidden = false,
  avatar = '',
  className = '',
  isAi = false,
  isChatBot = false,
  name = '',
  showInitials = true,
  size = 'small',
  testId = '',
}) => {
  if (!avatar && !name && !isAi) return null;

  const SIZES = {
    xsmall: {
      class: styles.isSizeXSmall,
      size: 24,
    },
    small: {
      class: styles.isSizeSmall,
      size: 34,
    },
    medium: {
      class: styles.isSizeMedium,
      size: 42,
    },
    large: {
      class: styles.isSizeLarge,
      size: 48,
    },
    xlarge: {
      class: styles.isSizeXLarge,
      size: 64,
    },
    jumbo: {
      class: styles.isSizeJumbo,
      size: 110,
    },
    fluid: {
      class: styles.isSizeFluid,
      size: 110,
    },
  };

  const getInitials = () => {
    const words = (name || '##').split(/[\s-]+/);
    let initials = words.map((word) => word.slice(0, 1).toUpperCase()).join('');
    if (initials.length > 3) {
      initials = initials.slice(0, 3);
    }
    if (SIZES[size].size <= 30) {
      initials = initials.slice(0, 2);
    }

    return initials;
  };

  const getInitialsBackgroundColor = () => {
    let hash = 0;
    if (name.length === 0) return 'black';

    if (isChatBot) {
      return getCssVariable({
        variableKey: '--ids-color-brand',
      });
    }

    name.split('').forEach((value, i) => {
      hash = (hash << 8) - hash + name.charCodeAt(i);
      hash |= 0;
    });

    hash = Math.abs(hash);
    return `#${hash.toString(16).slice(0, 6)}`;
  };

  const getInitialsStyle = () => {
    let setFontSize;

    if (getInitials().length === 1) {
      setFontSize = SIZES[size].size / 1.8;
    } else if (getInitials().length === 2) {
      setFontSize = SIZES[size].size / 2.6;
    } else if (getInitials().length === 3) {
      setFontSize = SIZES[size].size / 2.7;
    }

    setFontSize = Math.ceil(setFontSize);

    return {
      fontSize: `${setFontSize}px`,
      backgroundColor: getInitialsBackgroundColor(name || '##'),
    };
  };

  let children = null;
  if (isAi) {
    children = (
      <>
        <Icon icon="aiAvatar" size="50%" />
        <span className={styles.aiAccessibilityText}>{ariaLabel || i18n.t('ai', 'AI')}</span>
      </>
    );
  } else if (avatar) {
    children = <img alt={name} className={styles.image} src={avatar} aria-label={ariaLabel} />;
  } else if (showInitials) {
    children = (
      <>
        <span className={styles.initials} style={getInitialsStyle()} aria-hidden>
          {getInitials()}
        </span>
        {!ariaHidden && (
          <span className={styles.nameForInitials} aria-label={ariaLabel}>
            {name}
          </span>
        )}
      </>
    );
  } else {
    children = <Icon icon="avatar" size="50%" ariaLabel={ariaLabel} />;
  }

  return (
    <div
      aria-hidden={ariaHidden}
      className={classnames(
        styles.element,
        {
          [styles.hasIcon]: !avatar && !showInitials,
          [styles.isAi]: isAi,
          [SIZES[size].class]: size,
        },
        className,
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

Avatar.propTypes = {
  ariaLabel: PropTypes.string,
  ariaHidden: PropTypes.bool,
  avatar: PropTypes.string,
  className: PropTypes.string,
  isAi: PropTypes.bool,
  isChatBot: PropTypes.bool,
  name: PropTypes.string,
  showInitials: PropTypes.bool,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large', 'xlarge', 'jumbo', 'fluid']),
  testId: PropTypes.string,
};

export default Avatar;
